import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import HeroSection from '../../components/HeroSection';
import Input from '../../components/Input';
import { getUsernameFromJVCRM } from '../../services/UsernameChecking';
import { getEmailCheckingFromJVCRM } from '../../services/EmailChecking';
import { ticketBaseURL } from '../../configs/api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import '../../styles/Checkout.css';
import colorConfigs from '../../configs/colorConfigs';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { getPlans } from '../../services/Plan';
import { post_Checkout } from '../../services/subscription/checkout';
import { getActiveTaxes } from '../../services/Tax';

const Checkout = () => {
    const navigate = useNavigate();
    const [name, setName] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [streetAddress, setStreetAddress] = useState<string>("");
    const [city, setCity] = useState<string>("");
    const [state, setState] = useState<string>("");
    const [postalCode, setPostalCode] = useState<string>("");
    const [country, setCountry] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [card, setCard] = useState<string>("");
    const [cardHolderName, setCardHolderName] = useState<string>("");
    const [expirationDate, setExpirationDate] = useState<string>("");
    const [ccvCode, setCcvCode] = useState<string>("");
    const [userName, setUserName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean>(false);
    const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
    const [isEmailAvailable, setIsEmailAvailable] = useState<boolean>(false);
    const [showUsernameErrorMessage, setShowUsernameErrorMessage] = useState<boolean>(false);
    const [showUsernameSuccessMessage, setShowUsernameSuccessMessage] = useState<boolean>(false);
    const [showEmailErrorMessage, setShowEmailErrorMessage] = useState<boolean>(false);
    const [showEmailSuccessMessage, setShowEmailSuccessMessage] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
    const [isPasswordFocused, setIsPasswordFocused] = useState<boolean>(false);
    const [passwordTextType, setPasswordTextType] = useState<boolean>(false);
    const [couponCode, setCouponCode] = useState('');
    const [discountId, setDiscountId] = useState<number | null>(null);
    const [selectedMethod, setSelectedMethod] = useState('Credit Card');
    const [bankName, setBankName] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [accountType, setAccountType] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [bankPhoneNumber, setBankPhoneNumber] = useState('');
    const [accountHolderName, setAccountHolderName] = useState('');
    const [useAccountForFuture, setUseAccountForFuture] = useState(false);
    const [signedAuthorization, setSignedAuthorization] = useState(false);
    const [discountMessage, setDiscountMessage] = useState('');

    const [expirationDateError, setExpirationDateError] = useState("");
    const [isExpirationDateValid, setIsExpirationDateValid] = useState<boolean>(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);

    const location = useLocation();
    const { planId: plan, planName: pName, price: pPrice, billingCycle } = location.state || { planId: 0, planName: '', price: 0, billingCycle: '', source: '' };
    const [planId, setPlanId] = useState(plan);
    const [planName, setPlanName] = useState(pName);
    const [price, setPrice] = useState(pPrice);
    const [finalPrice, setFinalPrice] = useState(pPrice);
    const [taxes, setTaxes] = useState<any[]>([]);

    const [paymentError, setPaymentError] = useState<string>("");

    //fallbackPlanId
    useEffect(() => {
        if (planId <= 0) {
            getPlans()
                .then((data: any) => {
                    if (data?.length > 0) {
                        const plan = data[0];
                        setPlanId(plan?.id);
                        setPlanName(plan?.planName);
                        setPrice(plan?.price);
                    }
                })
                .catch(error => {
                    console.error('Error fetching plans data:', error)
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planId])

    useEffect(() => {
        getActiveTaxes()
            .then(res => {
                setTaxes(res);
            })
            .catch(error => {
                console.log(error);
            })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const taxDetails = useMemo(() => {
        return (taxes || []).map((tax: any) => ({
            name: tax.name,
            rate: tax.rate,
            amount: (Number(tax.rate) * finalPrice) / 100
        }));
    }, [taxes, finalPrice]);

    const taxAmount = useMemo(() => {
        return taxDetails.reduce((sum, tax) => sum + tax.amount, 0);
    }, [taxDetails]);

    const totalWithTax = useMemo(() => {
        return finalPrice + taxAmount;
    }, [finalPrice, taxAmount]);

    const handleMethodChange = (event: any) => {
        setSelectedMethod(event.target.value);
    };

    const handlePasswordChange = (value: string) => {
        setPassword(value);

        if (value.length > 0) {
            setIsPasswordFocused(true);
        } else {
            setIsPasswordFocused(false);
        }

        const isValidPassword =
            value.length >= 8 &&
            /\d/.test(value) &&
            /[a-z]/.test(value) &&
            /[A-Z]/.test(value) &&
            /[!@#$%^&*(),.?":{}|<>]/.test(value);

        setIsPasswordValid(isValidPassword);

        if (!isValidPassword && isPasswordFocused) {
            console.error("Password must be at least 8 characters long and include at least one lowercase letter (a-z), one uppercase letter (A-Z), one number (0-9), and one special character (!@#$%^&*()).");
        }
    };

    useEffect(() => {
        let debounceTimer: NodeJS.Timeout;

        if (userName.trim() !== '') {
            debounceTimer = setTimeout(() => {
                checkUsernameAvailability(userName.trim());
            }, 1000);
        } else {
            setIsUsernameAvailable(false);
            setShowUsernameErrorMessage(false);
            setShowUsernameSuccessMessage(false);
        }

        return () => clearTimeout(debounceTimer);
    }, [userName]);

    const checkUsernameAvailability = (username: string) => {
        getUsernameFromJVCRM(username)
            .then((response: boolean) => {
                setIsUsernameAvailable(!response);
                setShowUsernameErrorMessage(response);
                if (!response) {
                    setShowUsernameSuccessMessage(true);
                    setTimeout(() => {
                        setShowUsernameSuccessMessage(false);
                    }, 3000);
                }
                if (inputRef.current) {
                    inputRef.current.setCustomValidity(response ? "Username is already exist" : "");
                }
            })
            .catch((error: Error) => {
                console.error('Error checking username availability:', error);
                setIsUsernameAvailable(false);
                setShowUsernameErrorMessage(false);
                setShowUsernameSuccessMessage(false);
            });
    };

    useEffect(() => {
        let debounceTimer: NodeJS.Timeout;

        if (email.trim() !== '') {
            debounceTimer = setTimeout(() => {
                const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/gm.test(email.trim());
                setIsEmailValid(isValid);
                if (isValid) {
                    checkEmailAvailability(email.trim());
                } else {
                    setIsEmailAvailable(false);
                    setShowEmailErrorMessage(false);
                    setShowEmailSuccessMessage(false);
                }
            }, 1000);
        } else {
            setIsEmailValid(true);
            setIsEmailAvailable(false);
            setShowEmailErrorMessage(false);
            setShowEmailSuccessMessage(false);
        }

        return () => clearTimeout(debounceTimer);
    }, [email]);

    const checkEmailAvailability = (email: string) => {
        getEmailCheckingFromJVCRM(email)
            .then((response: boolean) => {
                setIsEmailAvailable(response);
                setShowEmailErrorMessage(response);
                setShowEmailSuccessMessage(!response);

                if (!response) {
                    setTimeout(() => {
                        setShowEmailSuccessMessage(false);
                    }, 3000);
                }

                if (inputRef.current) {
                    inputRef.current.setCustomValidity(response ? "Email is already exist" : "");
                }
            })
            .catch((error: Error) => {
                console.error('Error checking email availability:', error);
                setIsEmailAvailable(false);
                setShowEmailErrorMessage(false);
                setShowEmailSuccessMessage(false);
            });
    };

    const onChangePhoneNumber = (value: string, type: "phone" | "bankPhone") => {
        const numericValue = value.replace(/[^0-9]+/g, "");
        const trimmedValue = numericValue.slice(0, 10);
        let formattedValue = "";

        if (trimmedValue.length <= 3) {
            formattedValue = trimmedValue;
        } else if (trimmedValue.length <= 6) {
            formattedValue = `(${trimmedValue.slice(0, 3)}) ${trimmedValue.slice(3)}`;
        } else if (trimmedValue.length <= 10) {
            formattedValue = `(${trimmedValue.slice(0, 3)}) ${trimmedValue.slice(3, 6)}-${trimmedValue.slice(6)}`;
        }

        if (type === "phone") {
            setPhone(formattedValue);
        } else if (type === "bankPhone") {
            setBankPhoneNumber(formattedValue);
        }
    };

    const handlePostalCodeInputChange = (value: string) => {
        const numericValue = value.replace(/[^0-9]+/g, "");
        setPostalCode(numericValue);
    };

    const handleCardInputChange = (value: string) => {
        const numericValue = value.replace(/[^\d]+/g, "");
        setCard(numericValue);
    };

    const handleCardHolderNameInputChange = (value: string) => {
        const alphabeticValue = value.replace(/[^A-Za-z\s]+/g, "");
        setCardHolderName(alphabeticValue);
    };

    const handleExpirationDateInputChange = (value: string) => {
        const numericValue = value.replace(/[^0-9]+/g, "");
        let formattedValue = numericValue.slice(0, 4);
        let formattedDate = formattedValue;

        if (formattedValue.length >= 3) {
            formattedDate = formattedValue.replace(/^(\d{2})(\d{0,2})/, "$1/$2");
        }

        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = parseInt(currentDate.getFullYear().toString().slice(-2), 10);

        let errorMessage = "";
        let isValid = true;

        if (formattedValue.length === 4) {
            const enteredMonth = parseInt(formattedValue.slice(0, 2), 10);
            const enteredYear = parseInt(formattedValue.slice(2, 4), 10);

            if (enteredMonth < 1 || enteredMonth > 12) {
                errorMessage = "Invalid month. Please enter a month between 01 and 12.";
                isValid = false;
            } else if (enteredYear < currentYear || (enteredYear === currentYear && enteredMonth < currentMonth)) {
                errorMessage = "Expiration date must be in the future.";
                isValid = false;
            }
        } else if (formattedValue.length > 0 && formattedValue.length < 4) {
            errorMessage = "Please complete the expiration date in MM/YY format.";
            isValid = false;
        } else if (formattedValue.length === 0) {
            errorMessage = "Expiration date is required.";
            isValid = false;
        }

        setExpirationDate(formattedDate);
        setExpirationDateError(errorMessage);
        setIsExpirationDateValid(isValid);
    };

    useEffect(() => {
        if (selectedMethod !== 'Credit Card') {
            setCard("");
            setCardHolderName("");
            setExpirationDate("");
            setCcvCode("");
            setExpirationDateError("");
            setIsExpirationDateValid(true);
        }
    }, [selectedMethod]);

    const handleCcvCodeInputChange = (value: string) => {
        const numericValue = value.replace(/[^0-9]+/g, "");
        const formattedValue = numericValue.slice(0, 4);
        setCcvCode(formattedValue);
    };

    const applyDiscount = async () => {
        try {
            const response = await axios.get(`${ticketBaseURL}rdRentalDiscount/GetRdRentalDiscountByCouponCode?couponCode=${couponCode}`);
            const discountData = response.data;

            if (discountData) {
                let discountAmount = 0;

                if (discountData.discountType === 'Percentage') {
                    discountAmount = (price * discountData?.discountValue) / 100;
                } else if (discountData.discountType === 'Amount') {
                    discountAmount = discountData?.discountValue;
                }

                setFinalPrice(price - discountAmount);
                setDiscountMessage('Discount applied');
                setDiscountId(discountData?.id);
            } else {
                setDiscountMessage('Invalid coupon');
                setFinalPrice(price);
            }
        } catch (error) {
            setDiscountMessage('Invalid coupon');
            setFinalPrice(price);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isProcessing) {
            setIsProcessing(true);
            setPaymentError("");

            post_Checkout({
                planId: planId,
                discountId: discountId || 0,
                companyName: name,
                firstName: firstName,
                lastName: lastName,
                street: streetAddress,
                city: city,
                state: state,
                country: country,
                postalCode: postalCode,
                phone: phone,
                paymentType: selectedMethod,
                creditCardPayment: selectedMethod === "Credit Card"
                    ? {
                        creditCardNumber: card?.trim(),
                        cardHolderName: cardHolderName?.trim(),
                        ccvCode: ccvCode?.trim(),
                        expirationDate: expirationDate?.trim()
                    } : null,
                achPayment: selectedMethod === "Bank ACH"
                    ? {
                        bankName: bankName?.trim(),
                        accountNumber: bankAccountNumber?.trim(),
                        accountType: accountType,
                        routingNumber: routingNumber?.trim(),
                        bankPhone: bankPhoneNumber?.trim(),
                        accountHolderName: accountHolderName?.trim(),
                        useAccountInfoInFuture: useAccountForFuture,
                        signedAcknowledgement: signedAuthorization
                    } : null,
                userName: userName,
                email: email,
                password: password
            })
                .then(() => {
                    resetForm();
                })
                .catch(error => {
                    const errorMessage = error?.response?.data || error?.message || "Payment failed. Please check your information and try again.";
                    setIsProcessing(false);
                    setPaymentError(errorMessage);
                    console.error(error);
                });
        }
    }

    const resetForm = () => {
        setName('');
        setFirstName('');
        setLastName('');
        setTitle('');
        setStreetAddress('');
        setCity('');
        setState('');
        setPostalCode('');
        setCountry('');
        setPhone('');
        setCard('');
        setCardHolderName('');
        setExpirationDate('');
        setCcvCode('');
        setBankAccountNumber('');
        setAccountType('');
        setRoutingNumber('');
        setBankName('');
        setBankPhoneNumber('');
        setAccountHolderName('');
        setEmail('');
        setPassword('');
        setUserName('');
        setDiscountId(null);
        setSelectedMethod('');
        setUseAccountForFuture(false);
        setSignedAuthorization(false);
        setFinalPrice(0);
        setIsProcessing(false);
        navigate('/checkout/thank-you');
    };

    const getMessageColor = () => {
        return discountMessage === 'Discount applied' ? 'text-green-500' : 'text-red-500';
    };

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 640);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const dynamicStyles = {
        padding: "0.75rem 4rem",
        borderRadius: "0.125rem",
        fontWeight: "bold",
        transition: "background-color 300ms",
        backgroundColor:
            isProcessing
                ? "#2D3748"
                : isPasswordValid &&
                    (selectedMethod === "Credit Card"
                        ? isExpirationDateValid
                        : true)
                    ? "#167AC3"
                    : "#9CA3AF",
        color: "#FFFFFF",
        cursor:
            isPasswordValid &&
                (selectedMethod === "Credit Card"
                    ? isExpirationDateValid
                    : true)
                ? "pointer"
                : "not-allowed",
        ...(isProcessing ||
            !(isPasswordValid &&
                (selectedMethod === "Credit Card"
                    ? isExpirationDateValid
                    : true))
            ? {}
            : { ":hover": { backgroundColor: "#167AC3" } }),
    };

    return (
        <>
            <HeroSection mainHeading="Checkout" />
            <div
                style={{
                    display: 'flex',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                    justifyContent: 'center',
                    width: '100%',
                    backgroundColor: '#fff',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        paddingLeft: isSmallScreen ? '1.5rem' : '1rem',
                        paddingRight: isSmallScreen ? '1.5rem' : '1rem',
                        paddingTop: isSmallScreen ? '2.5rem' : '2rem',
                        paddingBottom: isSmallScreen ? '2.5rem' : '2rem',
                        maxWidth: isSmallScreen ? '100%' : '66.6667%',
                    }}
                >
                    <div
                        style={{
                            marginBottom: '1rem',
                            padding: '1rem',
                            backgroundColor: '#f1f5f9',
                            borderRadius: '0.375rem',
                        }}
                    >
                        <h3
                            style={{
                                color: colorConfigs.primaryColor,
                                fontWeight: '600',
                                fontSize: '1.5rem',
                                marginBottom: '1.5rem',
                            }}
                        >
                            Pricing Info
                        </h3>
                        <p>Membership: {planName}</p>
                        <p>Billing Cycle: <span className="capitalize">{billingCycle}</span></p>
                        <p>Base Price: ${price.toFixed(2)}</p>
                        {discountId && (
                            <p>Discount Applied: -${(price - finalPrice).toFixed(2)}</p>
                        )}

                        {taxDetails?.length > 0 && (
                            <div>
                                <p style={{ fontWeight: 600 }}>Taxes:</p>

                                {taxDetails.map((tax: any, index: number) => (
                                    <p key={index}>
                                        {tax.name} ({tax.rate}%): ${tax.amount.toFixed(2)}
                                    </p>
                                ))}
                            </div>
                        )}

                        <div
                            style={{
                                marginTop: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <input
                                type="text"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                                placeholder="Enter coupon code"
                                style={{
                                    border: '1px solid',
                                    borderColor: colorConfigs.primaryColor,
                                    padding: '0.5rem',
                                    fontSize: '0.875rem',
                                    flexGrow: 0,
                                    width: '50%',
                                }}
                            />
                            <button
                                onClick={applyDiscount}
                                style={{
                                    marginTop: '0.5rem',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    backgroundColor: colorConfigs.primaryColor,
                                    color: '#ffffff',
                                    borderRadius: '0.25rem',
                                    marginLeft: '0',
                                }}
                            >
                                Apply Discount
                            </button>
                        </div>
                        {discountMessage && (
                            <div style={{ marginTop: '1rem' }}>
                                <p className={getMessageColor()}>{discountMessage}</p>
                            </div>
                        )}

                        <div style={{ marginTop: '1rem' }}>
                            <p
                                style={{
                                    fontSize: '1.125rem',
                                    fontWeight: '600',
                                }}
                            >
                                Total Price: ${totalWithTax?.toFixed(2)}
                            </p>
                        </div>
                    </div>


                    <h1
                        style={{
                            color: colorConfigs.primaryColor,
                            fontWeight: '600',
                            fontSize: '1.5rem',
                            marginTop: '1.5rem',
                            marginBottom: '1.5rem',
                        }}>
                        My Address
                    </h1>

                    <form className="form-container" onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '0.25rem' }}>
                            <Input
                                type="text"
                                label="Company or Organization"
                                pattern=""
                                title=""
                                inputValue={name}
                                setInputValue={setName}
                            />

                            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
                                <Input
                                    type="text"
                                    label="First Name"
                                    pattern=""
                                    title=""
                                    inputValue={firstName}
                                    setInputValue={setFirstName}
                                />

                                <Input
                                    type="text"
                                    label="Last Name"
                                    pattern=""
                                    title=""
                                    inputValue={lastName}
                                    setInputValue={setLastName}
                                />
                            </div>

                            <Input
                                type="text"
                                label="Title"
                                pattern=""
                                title=""
                                inputValue={title}
                                setInputValue={setTitle}
                            />

                            <Input
                                type="text"
                                label="Street Address"
                                pattern=""
                                title=""
                                inputValue={streetAddress}
                                setInputValue={setStreetAddress}
                            />

                            <Input
                                type="text"
                                label="City"
                                pattern=""
                                title=""
                                inputValue={city}
                                setInputValue={setCity}
                            />

                            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
                                <Input
                                    type="text"
                                    label="State / Province"
                                    pattern=""
                                    title=""
                                    inputValue={state}
                                    setInputValue={setState}
                                />

                                <Input
                                    type="text"
                                    label="Postal Code"
                                    pattern="^\d*$"
                                    title=""
                                    inputValue={postalCode}
                                    setInputValue={handlePostalCodeInputChange}
                                />

                            </div>

                            <Input
                                type="text"
                                label="Country"
                                pattern=""
                                title=""
                                inputValue={country}
                                setInputValue={setCountry}
                            />

                            <Input
                                type="text"
                                label="Phone Number"
                                pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                title="Please Enter a 10 digits numbers."
                                inputValue={phone}
                                setInputValue={(value) => onChangePhoneNumber(value, "phone")}
                            />
                        </div>


                        <div style={{ marginBottom: '0.25rem' }}>
                            <div style={{ marginBottom: '0.25rem', padding: '0.75rem', backgroundColor: '#f1f5f9', borderRadius: '0.375rem' }}>
                                <h3
                                    style={{
                                        color: colorConfigs.primaryColor,
                                        fontWeight: '600',
                                        fontSize: '1.5rem',
                                        marginBottom: '1.5rem',
                                    }}
                                >
                                    Payment Info
                                </h3>

                                <div
                                    style={{
                                        marginBottom: '2rem',
                                        display: 'flex',
                                        gap: '1rem',
                                        alignItems: isSmallScreen ? 'flex-start' : 'center',
                                        flexDirection: 'row',
                                    }}
                                >
                                    <label
                                        style={{
                                            color: colorConfigs.primaryColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            value="Credit Card"
                                            checked={selectedMethod === "Credit Card"}
                                            onChange={handleMethodChange}
                                            className="mr-2"
                                        />
                                        Credit Card
                                    </label>

                                    <label
                                        style={{
                                            color: colorConfigs.primaryColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            value="Bank ACH"
                                            checked={selectedMethod === "Bank ACH"}
                                            onChange={handleMethodChange}
                                            className="mr-2"
                                        />
                                        Bank ACH
                                    </label>
                                </div>

                                {selectedMethod === 'Credit Card' && (
                                    <>
                                        <Input
                                            type="text"
                                            label="Credit or debit card"
                                            pattern="[0-9]*"
                                            title=""
                                            inputValue={card}
                                            setInputValue={handleCardInputChange}
                                        />

                                        <Input
                                            type="text"
                                            label="Cardholder name"
                                            pattern="[A-Za-z\s]*"
                                            title=""
                                            inputValue={cardHolderName}
                                            setInputValue={handleCardHolderNameInputChange}
                                        />

                                        <div className="payment-info-container">
                                            <Input
                                                type="text"
                                                label="Expiration Date"
                                                pattern="^(0[1-9]|1[0-2])\/\d{1,4}$"
                                                title="Please enter a valid expiration date in the format MM/YY"
                                                inputValue={expirationDate}
                                                setInputValue={handleExpirationDateInputChange}
                                            />

                                            <Input
                                                type="text"
                                                label="CCV Code"
                                                pattern="[0-9]*"
                                                title=""
                                                inputValue={ccvCode}
                                                setInputValue={handleCcvCodeInputChange}
                                            />
                                        </div>
                                        {expirationDateError && !isExpirationDateValid && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginTop: '0.25rem',
                                                    fontSize: '0.75rem',
                                                    color: '#D92D20',
                                                }}
                                            >
                                                <span>{expirationDateError}</span>
                                            </div>
                                        )}
                                    </>
                                )}

                                {selectedMethod === 'Bank ACH' && (
                                    <>
                                        <div className="payment-info-container">
                                            <Input
                                                type="text"
                                                label="Bank Name"
                                                pattern=""
                                                title=""
                                                inputValue={bankName}
                                                setInputValue={setBankName}
                                            />

                                            <Input
                                                type="text"
                                                label="Account Number"
                                                pattern=""
                                                title=""
                                                inputValue={bankAccountNumber}
                                                setInputValue={setBankAccountNumber}
                                            />
                                        </div>

                                        <div className="payment-info-container">
                                            <Input
                                                type="select"
                                                label="Account Type"
                                                pattern=""
                                                title=""
                                                options={[
                                                    { value: '', label: 'Account Type' },
                                                    { value: 'checking', label: 'Checking' },
                                                    { value: 'saving', label: 'Saving' },
                                                ]}
                                                inputValue={accountType}
                                                setInputValue={setAccountType}
                                            />

                                            <Input
                                                type="text"
                                                label="Routing Number"
                                                pattern=""
                                                title=""
                                                inputValue={routingNumber}
                                                setInputValue={setRoutingNumber}
                                            />
                                        </div>

                                        <div className="payment-info-container">
                                            <Input
                                                type="text"
                                                label="Account Holder Name"
                                                pattern=""
                                                title=""
                                                inputValue={accountHolderName}
                                                setInputValue={setAccountHolderName}
                                            />

                                            <Input
                                                type="text"
                                                label="Bank Phone Number"
                                                pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                                title="Please Enter a 10 digits number."
                                                inputValue={bankPhoneNumber}
                                                setInputValue={(value) => onChangePhoneNumber(value, "bankPhone")}
                                            />
                                        </div>

                                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.5rem" }}>
                                            <label style={{ display: "flex", alignItems: "flex-start" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={useAccountForFuture}
                                                    onChange={(e) => setUseAccountForFuture(e.target.checked)}
                                                    className="mt-1.5"
                                                />
                                                <span style={{ marginLeft: "0.5rem" }}>Use this account information in the future</span>
                                            </label>

                                            <label style={{ display: "flex", alignItems: "flex-start" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={signedAuthorization}
                                                    onChange={(e) => setSignedAuthorization(e.target.checked)}
                                                    className="mt-1.5"
                                                    required
                                                />
                                                <span style={{ marginLeft: "0.5rem" }}>
                                                    I authorize the above business to debit my bank account as outlined
                                                    in the payment terms of this agreement.
                                                </span>
                                            </label>
                                        </div>
                                    </>
                                )}

                                {paymentError && <p className="test-[0.75rem] font-medium text-red-500 mt-[1.25rem]">{paymentError}</p>}
                            </div>

                            <h3 style={{ color: colorConfigs.primaryColor, fontWeight: "600", fontSize: "1.5rem", margin: "1rem 0", paddingTop: "10px", paddingBottom: "20px" }}>
                                Create Account
                            </h3>

                            <Input
                                type="text"
                                label="Username"
                                pattern=""
                                title=""
                                inputValue={userName}
                                setInputValue={setUserName}
                                autoComplete="new-username"
                            />
                            <div style={{ marginBottom: '1rem' }}>
                                {showUsernameSuccessMessage && isUsernameAvailable === true && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginTop: '-1.5rem',
                                            marginBottom: '2rem',
                                            fontSize: '0.75rem',
                                            color: '#22c55e',
                                        }}
                                    >
                                        <CheckCircleOutlineIcon
                                            style={{
                                                marginRight: '0.25rem',
                                                fontSize: '16px',
                                            }}
                                        />
                                        <span>Username is available</span>
                                    </div>
                                )}
                                {showUsernameErrorMessage && isUsernameAvailable === false && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginTop: '-1.5rem',
                                            marginBottom: '2rem',
                                            fontSize: '0.75rem',
                                            color: '#D92D20',
                                        }}
                                    >
                                        <ErrorOutlineIcon
                                            style={{
                                                marginRight: '0.25rem',
                                                fontSize: '16px',
                                            }}
                                        />
                                        <span>Username is already exist</span>
                                    </div>
                                )}
                            </div>

                            <Input
                                type="email"
                                label="Email"
                                pattern=""
                                title=""
                                inputValue={email}
                                setInputValue={setEmail}
                                autoComplete="new-email"
                            />
                            {!isEmailValid && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginTop: '-1.5rem',
                                        marginBottom: '2rem',
                                        fontSize: '0.75rem',
                                        color: '#D92D20',
                                    }}
                                >
                                    <ErrorOutlineIcon
                                        style={{
                                            marginRight: '0.25rem',
                                            fontSize: '16px',
                                        }}
                                    />
                                    <span>Please enter a valid email address</span>
                                </div>
                            )}

                            {showEmailSuccessMessage && !isEmailAvailable && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginTop: '-1.5rem',
                                        marginBottom: '2rem',
                                        fontSize: '0.75rem',
                                        color: '#22c55e',
                                    }}
                                >
                                    <CheckCircleOutlineIcon
                                        style={{
                                            marginRight: '0.25rem',
                                            fontSize: '16px',
                                        }}
                                    />
                                    <span>Email is available</span>
                                </div>
                            )}

                            {showEmailErrorMessage && isEmailAvailable && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginTop: '-1.5rem',
                                        marginBottom: '2rem',
                                        fontSize: '0.75rem',
                                        color: '#D92D20',
                                    }}
                                >
                                    <ErrorOutlineIcon
                                        style={{
                                            marginRight: '0.25rem',
                                            fontSize: '16px',
                                        }}
                                    />
                                    <span>Email is already exist</span>
                                </div>
                            )}
                            <div style={{ position: "relative" }}>
                                <Input
                                    type={passwordTextType ? 'text' : 'password'}
                                    label="Password"
                                    pattern=""
                                    title=""
                                    inputValue={password}
                                    setInputValue={handlePasswordChange}
                                    autoComplete="new-password"
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        right: "1rem",
                                        top: "40%",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        width: "18px",
                                        height: "18px",
                                    }}
                                    onClick={() => setPasswordTextType((prev) => !prev)}
                                >
                                    {passwordTextType ? (
                                        <VisibilityOff style={{ cursor: 'pointer', color: '#bbb' }} />
                                    ) : (
                                        <Visibility style={{ cursor: 'pointer', color: '#bbb' }} />
                                    )}
                                </div>
                            </div>
                            {!isPasswordValid && password.length > 0 && (
                                <p
                                    style={{
                                        color: '#D92D20',
                                        fontSize: '0.75rem',
                                        marginTop: '-1.5rem',
                                    }}
                                >
                                    Password must be at least 8 characters long and include at least one lowercase letter (a-z),
                                    one uppercase letter (A-Z),
                                    one number (0-9),
                                    and one special character (!@#$%^&*()).
                                </p>
                            )}

                            <div style={{ margin: "2.5rem 0", textAlign: "center" }}>
                                <button
                                    type="submit"
                                    style={dynamicStyles}
                                >
                                    {isProcessing ? "Processing..." : "Place Order Now"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default Checkout;